import * as THREE from 'three'
import type { VisualPartRole } from './fighterVisualIntegrity'
import type { PerceptualMorphogenesisFrame } from '@inyron/animation'

export type MaterialMemoryInput={
  health01:number
  impact01:number
  combo01:number
  active01:number
  formBoost01:number
  perceptual?:PerceptualMorphogenesisFrame
  dt:number
}
export type MaterialMemoryFrame={
  wear01:number
  heat01:number
  stress01:number
}
type Binding={
  material:THREE.MeshStandardMaterial
  role:VisualPartRole
  baseColor:THREE.Color
  baseEmissive:THREE.Color
  baseRoughness:number
  baseMetalness:number
  baseEmissiveIntensity:number
  baseEnvMapIntensity:number
}
const clamp01=(v:number)=>Math.max(0,Math.min(1,v))
export function deriveMaterialMemory(input:MaterialMemoryInput):MaterialMemoryFrame{
  const damage=1-clamp01(input.health01)
  const impact=clamp01(input.impact01)
  const combo=clamp01(input.combo01)
  const active=clamp01(input.active01)
  const form=clamp01(input.formBoost01)
  return{
    wear01:clamp01(damage*.78+impact*.14+combo*.08),
    heat01:clamp01(impact*.5+active*.18+combo*.16+form*.34),
    stress01:clamp01(damage*.55+impact*.28+combo*.1+active*.07),
  }
}
const roleWear=(role:VisualPartRole)=>role==='garment'?1:role==='body'?.72:role==='armor'?.64:role==='weapon'?.5:role==='accessory'?.58:.35
const roleHeat=(role:VisualPartRole)=>role==='weapon'?1:role==='armor'?.82:role==='body'?.48:role==='garment'?.3:role==='accessory'?.5:.28
export function createFighterMaterialMemory(root:THREE.Object3D,accent:string){
  const bindings:Binding[]=[]
  const accentColor=new THREE.Color(accent)
  const wearTint=new THREE.Color('#241815')
  root.traverse(node=>{
    if(!(node instanceof THREE.Mesh))return
    const role=(node.userData.visualPartRole??'unknown') as VisualPartRole
    const materials=Array.isArray(node.material)?node.material:[node.material]
    for(const material of materials){
      if(!(material instanceof THREE.MeshStandardMaterial))continue
      bindings.push({
        material,
        role,
        baseColor:material.color.clone(),
        baseEmissive:material.emissive.clone(),
        baseRoughness:material.roughness,
        baseMetalness:material.metalness,
        baseEmissiveIntensity:material.emissiveIntensity,
        baseEnvMapIntensity:Number.isFinite(material.envMapIntensity)?material.envMapIntensity:1,
      })
    }
  })
  let smoothedWear=0,smoothedHeat=0,smoothedStress=0
  return{
    size:bindings.length,
    update(input:MaterialMemoryInput){
      const frame=deriveMaterialMemory(input),damp=1-Math.exp(-Math.max(0,input.dt)*7)
      smoothedWear=THREE.MathUtils.lerp(smoothedWear,frame.wear01,damp)
      smoothedHeat=THREE.MathUtils.lerp(smoothedHeat,frame.heat01,damp)
      smoothedStress=THREE.MathUtils.lerp(smoothedStress,frame.stress01,damp)
      const perceptual=input.perceptual
      for(const binding of bindings){
        const wear=smoothedWear*roleWear(binding.role),heat=smoothedHeat*roleHeat(binding.role)
        const roleMicro=binding.role==='weapon'||binding.role==='armor'?1:binding.role==='accessory'?.82:.62
        const chroma=(perceptual?.chromaMicroShift??0)*roleMicro
        binding.material.color.copy(binding.baseColor).lerp(wearTint,wear*.14).offsetHSL(chroma*.12,0,0)
        binding.material.emissive.copy(binding.baseEmissive).lerp(accentColor,heat*.34+(perceptual?.iridescenceShift??0)*.035*roleMicro)
        binding.material.emissiveIntensity=binding.baseEmissiveIntensity+heat*.38+smoothedStress*.05+(perceptual?.emissivePulse??0)*.42*roleMicro
        binding.material.roughness=THREE.MathUtils.clamp(binding.baseRoughness+wear*.13-heat*.035+(perceptual?.materialRoughnessDelta??0)*roleMicro,.16,.92)
        binding.material.metalness=THREE.MathUtils.clamp(binding.baseMetalness+(binding.role==='weapon'||binding.role==='armor'?heat*.035:0)+(perceptual?.materialMetalnessDelta??0)*roleMicro,0,1)
        binding.material.envMapIntensity=THREE.MathUtils.clamp(binding.baseEnvMapIntensity*(1+(perceptual?.edgeLuminance??0)*.24*roleMicro),0,4)
      }
      return{wear01:smoothedWear,heat01:smoothedHeat,stress01:smoothedStress}
    },
  }
}
